import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import LanguageSwitcher from './common/LanguageSwitcher';
import NotificationBell from './notifications/NotificationBell';

const Navigation: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Don't show navigation on landing page
  if (location.pathname === '/') {
    return null;
  }

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <nav
      className="sticky top-0 z-50 bg-dark-bg shadow-dark-sm border-b border-dark-border"
      aria-label="Main navigation"
    >
      <div className="max-w-screen-xl mx-auto px-5 flex justify-between items-center py-4">
        <Link
          to="/"
          className="text-2xl font-bold text-dark-primary-light no-underline"
          aria-label="oBusinessCard - Go to homepage"
        >
          oBusinessCard
        </Link>

        {/* Hamburger button - visible on mobile only */}
        <button
          className="md:hidden text-dark-text p-2 rounded-md hover:bg-dark-bg-secondary transition-colors"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {/* Desktop nav links - hidden on mobile, flex on md+ */}
        <div className="hidden md:flex items-center gap-6" role="list">
          <Link
            to="/pricing"
            className="text-dark-text-secondary text-sm font-medium no-underline hover:text-dark-primary-light transition-colors"
            role="listitem"
          >
            {t('nav.pricing')}
          </Link>

          {user ? (
            <>
              <Link
                to="/dashboard"
                className="text-dark-text-secondary text-sm font-medium no-underline hover:text-dark-primary-light transition-colors"
                role="listitem"
              >
                {t('nav.dashboard')}
              </Link>
              <Link
                to="/card-creation"
                className="text-dark-text-secondary text-sm font-medium no-underline hover:text-dark-primary-light transition-colors"
                role="listitem"
              >
                {t('card_creation.title')}
              </Link>
              <Link
                to="/gifts"
                className="text-dark-text-secondary text-sm font-medium no-underline hover:text-dark-primary-light transition-colors"
                role="listitem"
              >
                {t('nav.gifts')}
              </Link>
              <Link
                to="/team"
                className="text-dark-text-secondary text-sm font-medium no-underline hover:text-dark-primary-light transition-colors"
                role="listitem"
              >
                Team
              </Link>
              <NotificationBell />
              <LanguageSwitcher />
              <button
                onClick={logout}
                className="bg-transparent border-none text-dark-text-secondary text-sm font-medium cursor-pointer px-4 py-2 rounded-md hover:bg-dark-bg-secondary transition-colors"
                aria-label="Log out of your account"
              >
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-dark-text-secondary text-sm font-medium no-underline hover:text-dark-primary-light transition-colors"
                role="listitem"
              >
                {t('nav.login')}
              </Link>
              <LanguageSwitcher />
              <Link
                to="/register"
                className="bg-dark-primary text-white text-sm font-medium no-underline px-4 py-2 rounded-md hover:bg-dark-primary-dark transition-colors"
                role="listitem"
              >
                {t('nav.sign_up')}
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-dark-border bg-dark-bg-secondary px-5 py-4 flex flex-col gap-3">
          <Link
            to="/pricing"
            className="text-dark-text-secondary text-sm font-medium no-underline py-2 hover:text-dark-primary-light transition-colors"
            onClick={closeMobileMenu}
          >
            {t('nav.pricing')}
          </Link>

          <LanguageSwitcher />

          {user ? (
            <>
              <Link
                to="/dashboard"
                className="text-dark-text-secondary text-sm font-medium no-underline py-2 hover:text-dark-primary-light transition-colors"
                onClick={closeMobileMenu}
              >
                {t('nav.dashboard')}
              </Link>
              <Link
                to="/card-creation"
                className="text-dark-text-secondary text-sm font-medium no-underline py-2 hover:text-dark-primary-light transition-colors"
                onClick={closeMobileMenu}
              >
                {t('card_creation.title')}
              </Link>
              <Link
                to="/gifts"
                className="text-dark-text-secondary text-sm font-medium no-underline py-2 hover:text-dark-primary-light transition-colors"
                onClick={closeMobileMenu}
              >
                {t('nav.gifts')}
              </Link>
              <Link
                to="/team"
                className="text-dark-text-secondary text-sm font-medium no-underline py-2 hover:text-dark-primary-light transition-colors"
                onClick={closeMobileMenu}
              >
                Team
              </Link>
              <button
                onClick={() => { logout(); closeMobileMenu(); }}
                className="bg-transparent border-none text-dark-text-secondary text-sm font-medium cursor-pointer py-2 text-left hover:text-dark-primary-light transition-colors"
                aria-label="Log out of your account"
              >
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-dark-text-secondary text-sm font-medium no-underline py-2 hover:text-dark-primary-light transition-colors"
                onClick={closeMobileMenu}
              >
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="bg-dark-primary text-white text-sm font-medium no-underline px-4 py-2 rounded-md text-center hover:bg-dark-primary-dark transition-colors"
                onClick={closeMobileMenu}
              >
                {t('nav.sign_up')}
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navigation;
